<script>
import FormModal from "@/components/form-modal.vue";
import MasterDetail from "@/components/master-detail.vue";
import { mapGetters } from "vuex";
import { AnyAdmin } from "@/core/enums/user-types";
import { TiposResponsaveisEnum } from "../responsaveis/responsaveis.types";

const createEmptyFormData = () => ({
  nivel_hierarquico_id: null,
  business_unit_id: null,
  nome: "",
  email: ""
});

export default {
  components: {
    FormModal,
    MasterDetail
  },
  data() {
    return {
      createModal: {
        data: createEmptyFormData(),
        opened: false,
        title: ""
      },
      enabledEmail: false,
      opts: {
        businessUnits: [],
        niveisHierarquicos: [],
        acessos: [
          { id: true, nome: "Sim" },
          { id: false, nome: "Não" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["clientId", "user"]),
    actionBarButtons() {
      const show = this.podeEditar;
      const buttons = [
        {
          text: "Adicionar",
          icon: "mdi-plus-box-outline",
          show,
          action: () => {
            this.enabledEmail = true;
            this.createModal.data = createEmptyFormData();
            this.createModal.title = "Cadastro de Responsável";
            this.createModal.opened = true;
          }
        }
      ];

      return buttons.filter(({ show }) => show);
    },
    cols() {
      const self = this;

      return [
        {
          key: "usuario",
          name: "Possui acesso?",
          type: this.$fieldTypes.SELECT,
          rel: { to: "acessos", key: "id", name: "nome" },
          hideInform: true,
          filterable: true,
          width: "50px"
        },
        {
          key: "nivel_hierarquico_id",
          name: "Nível hierarquico",
          rules: [{ rule: "required" }],
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "niveisHierarquicos", key: "id", name: "nome" },
          filterable: true,
          colSize: 6
        },
        {
          key: "business_unit_id",
          name: "Business Unit",
          rules: [{ rule: "required" }],
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "businessUnits", key: "id", name: "label" },
          filterable: true,
          colSize: 6
        },
        {
          key: "nome",
          name: "Nome",
          type: this.$fieldTypes.TEXT,
          rules: [{ rule: "required" }],
          filterable: true
        },
        {
          key: "email",
          name: "E-mail",
          type: this.$fieldTypes.TEXT,
          rules: [{ rule: "required" }],
          editable: self.enabledEmail,
          filterable: true
        }
      ];
    },
    customResource() {
      const resource = this.apiResource(
        `v1/ativacaoped/${this.clientId}/responsaveis`
      );
      return {
        ...resource,
        async get(...args) {
          const { data, ...rest } = await resource.get(...args);
          return {
            ...rest,
            data: data.map(d => ({ ...d, usuario: !!d.usuario_id }))
          };
        }
      };
    },
    /**
     * Quem pode criar e editar responsáveis: Galapos (Administrador/Consultor) ou
     * responsável com nível hierárquico Administrador — a mesma regra do "Adicionar".
     * @type {boolean}
     * @readonly
     */
    podeEditar() {
      return AnyAdmin.includes(this.user.tipo_usuario) || this.hasAdminRole;
    },
    /**
     * Determina se o usuário logado é um responsável com o nível hierarquico FP&A
     * Testando o label para funcionar para o cliente XP Inc. Não pensado para outros clientes.
     * @type {boolean}
     * @readonly
     */
    hasAdminRole() {
      return (
        this.user.nivel_hierarquico?.tipo_id ===
        TiposResponsaveisEnum.ADMINISTRADOR
      );
    }
  },
  async created() {
    if (this.clientId === 0) {
      this.goToHome();
      return;
    }

    const promises = [this.getBusinessUnits(), this.getNiveisHierarquicos()];
    await Promise.all(promises);
  },
  watch: {
    clientId(value) {
      if (value === 0) {
        this.goToHome();
        return;
      }

      this.getBusinessUnits();
      this.getNiveisHierarquicos();
    }
  },
  methods: {
    async doExport() {
      try {
        const { get } = this.apiResource(
          `/v1/ativacaoped/${this.clientId}/responsaveis/export`
        );
        const response = await get();

        if (!response.url) {
          throw new Error();
        }

        const a = document.createElement("a");
        a.href = response.url;
        a.target = "_blank";
        a.click();
        a.remove();
      } catch {
        this.notify({
          message: "Não foi possível baixar o modelo. Tente novamente."
        });
      }
    },
    doImport() {
      this.$refs.inputFile?.click();
    },
    async doSave(data, close) {
      try {
        await this.customResource.save(data);

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Registro adicionado com sucesso",
          text: `Um novo responsável foi adicionado com sucesso.`
        });
        this.$refs.masterDetail?.doLoad();

        if (close) {
          close();
        }
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    async getBusinessUnits() {
      try {
        const { get } = this.apiResource(
          `v1/ativacaoped/${this.clientId}/business-units/selecao`
        );
        const response = await get();
        this.opts.businessUnits = response.map(bu => ({
          ...bu,
          label: `${bu.nome}${bu.divisao?.nome ? ` (${bu.divisao.nome})` : ''}`
        }));
      } catch (error) {
        this.notify(error);
        this.opts.businessUnits = [];
      }
    },
    async getNiveisHierarquicos() {
      try {
        const { get } = this.apiResource(
          `v1/ativacaoped/${this.clientId}/nivel-hierarquicos/selecao`
        );
        const response = await get();
        this.opts.niveisHierarquicos = response.sort(
          ({ nome: a }, { nome: b }) => a.localeCompare(b)
        );
      } catch (error) {
        this.notify(error);
        this.opts.niveisHierarquicos = [];
      }
    },
    goToHome() {
      this.notify({
        error:
          "Não é possível acessar o módulo de Ativação P&D sem uma empresa selecionada."
      });
      this.$router.push({ name: "homepage" });
    },
    async onInputChange(event) {
      const [file] = Array.from(event.target.files);
      const MAX_FILE_SIZE = 10; // 10 MegaBytes

      if (file.size > MAX_FILE_SIZE * 1024 * 1024) {
        this.notify({
          error: `O arquivo ${file.name} excedeu o limite de ${MAX_FILE_SIZE}MB e não foi importado.`
        });
        return;
      }

      try {
        this.$root.$emit("addLoadingProcess", "responsaveisimport");
        const api = this.api();
        const url = `/v1/ativacaoped/${this.clientId}/business-plans/importar`;
        const formData = new FormData();
        formData.append("arquivo", file);
        formData.append("tipo", "responsaveis");

        await api.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Importação concluída",
          text: `O arquivo está na fila para ser analisado e dentro de instantes você receberá uma notificação do sistema.`
        });
        this.$refs.masterDetail?.doLoad();
      } catch (error) {
        this.notify(error);
      } finally {
        this.$root.$emit("removeLoadingProcess", "responsaveisimport");
      }

      event.target.value = null; // Importante para poder voltar a anexar mais arquivos
    }
  }
};
</script>

<template>
  <MasterDetail
    ref="masterDetail"
    :actionBarButtons="actionBarButtons"
    :canDelete="false"
    :canEditByRole="podeEditar"
    :cols="cols"
    :customResource="customResource"
    formTitle="Responsável"
    :hasExportCSV="false"
    :hasExportXLSX="false"
    :hasNewButton="false"
    :opts="opts"
    serverPagination
    @onOpenFormDialog="enabledEmail = !$event.id"
  >
    <template v-slot:item.usuarionome="{ item }">
      <div class="mx-auto" style="display: table">
        <v-icon :color="item.usuario ? '#135153' : '#EC4C47'">
          {{ item.usuario ? "mdi-check-circle" : "mdi-close-circle" }}
        </v-icon>
      </div>
    </template>

    <input
      ref="inputFile"
      accept=".xlsx"
      class="input-file"
      type="file"
      @change="onInputChange"
    />

    <div class="d-flex">
      <v-spacer></v-spacer>
      <div
        aria-label="exportButton"
        class="table-v-action-button mr-3 pt-1"
        @click="doExport()"
      >
        <v-icon>mdi-download-box-outline</v-icon> Exportar modelo
      </div>
      <div
        aria-label="importButton"
        class="table-v-action-button mr-3 pt-1"
        @click="doImport()"
      >
        <v-icon>mdi-upload-box-outline</v-icon> Importações
      </div>
    </div>

    <FormModal
      v-model="createModal.opened"
      :cols="cols"
      :opts="opts"
      :title="createModal.title"
      :value.sync="createModal.data"
      @save="doSave"
    />
  </MasterDetail>
</template>

<style lang="scss" scoped>
.input-file {
  display: none;
}
</style>
